import type { BotLocale } from '@apaxir/shared';

/**
 * UI chrome translations only — button labels, placeholders, errors, confirm.
 *
 * Bot-specific content (name, welcome, suggested prompts) lives in KV and
 * is fetched per-bot by the widget at mount. Same widget.js serves every
 * client bot; only chrome strings are baked into the bundle.
 */

// Re-use BotLocale as the widget UI locale. Having one type keeps the
// resolveBotContent → widget.t pipeline single-source-of-truth.
export type Locale = BotLocale;

export interface Translations {
  inputPlaceholder: string;
  sendButton: string; // aria-label
  closeButton: string; // aria-label
  restartButton: string; // aria-label
  restartConfirm: string; // browser confirm() text
  chatLauncher: string; // aria-label for bubble
  typingIndicator: string; // aria-label for ...
  errorGeneric: string;
  errorBotNotFound: string;
  errorConfigMissing: string;
  errorNoApiKey: string;
  errorMonthlyCapExceeded: string;
  errorRateLimited: (seconds: number) => string;
  poweredBy: string;
  // Phase 3d.1 — citations footer
  citationsLabel: string; // plural ("Sources")
  citationLabelSingular: string; // singular ("Source")
  // Phase 3f.1 — abuse-prevention errors
  errorMessageTooLong: (limit: number) => string;
  errorConversationLimitReached: string;
  // Phase 3f.2 — daily quota + cap-disable
  errorDailyQuotaExceeded: string;
  errorBotTemporarilyUnavailable: string;
  // Phase 3f.3 — Turnstile failure
  errorTurnstileFailed: string;
}

export const translations: Record<Locale, Translations> = {
  en: {
    inputPlaceholder: 'Write a message...',
    sendButton: 'Send message',
    closeButton: 'Close chat',
    restartButton: 'Restart conversation',
    restartConfirm: 'Start a new conversation? Current messages will be cleared.',
    chatLauncher: 'Open chat',
    typingIndicator: 'Assistant is typing',
    errorGeneric: 'Something went wrong. Please try again.',
    errorBotNotFound: 'This chatbot is not available.',
    errorConfigMissing: 'Chatbot is misconfigured. Please contact the site owner.',
    errorNoApiKey: 'Chatbot is not configured. Please contact the site owner.',
    errorMonthlyCapExceeded: "This chatbot has reached its monthly limit. Please try again next month or contact the site owner.",
    errorRateLimited: (s) => `You're sending messages too fast. Try again in ${s}s.`,
    poweredBy: 'Powered by Apaxir',
    citationsLabel: 'Sources:',
    citationLabelSingular: 'Source:',
    errorMessageTooLong: (n) => `Message too long (max ${n} characters).`,
    errorConversationLimitReached:
      'This conversation has reached its limit. Please start a new one.',
    errorDailyQuotaExceeded:
      'Daily message limit reached. Try again tomorrow.',
    errorBotTemporarilyUnavailable:
      'Chatbot is temporarily unavailable. Please try again later.',
    errorTurnstileFailed:
      'Verification failed. Please refresh and try again.',
  },
  uk: {
    inputPlaceholder: 'Напишіть повідомлення...',
    sendButton: 'Надіслати повідомлення',
    closeButton: 'Закрити чат',
    restartButton: 'Почати нову розмову',
    restartConfirm: 'Почати нову розмову? Поточні повідомлення буде очищено.',
    chatLauncher: 'Відкрити чат',
    typingIndicator: 'Асистент друкує',
    errorGeneric: 'Щось пішло не так. Спробуйте ще раз.',
    errorBotNotFound: 'Чат-бот наразі недоступний.',
    errorConfigMissing: 'Чат-бот налаштовано некоректно. Зверніться до власника сайту.',
    errorNoApiKey: 'Чат-бот не налаштовано. Зверніться до власника сайту.',
    errorMonthlyCapExceeded: 'Чат-бот досяг місячного ліміту. Спробуйте наступного місяця або зверніться до власника сайту.',
    errorRateLimited: (s) => `Ви надсилаєте повідомлення надто швидко. Спробуйте за ${s} с.`,
    poweredBy: 'Працює на Apaxir',
    citationsLabel: 'Джерела:',
    citationLabelSingular: 'Джерело:',
    errorMessageTooLong: (n) => `Повідомлення занадто довге (макс ${n} символів).`,
    errorConversationLimitReached:
      'Розмова досягла ліміту повідомлень. Почніть нову.',
    errorDailyQuotaExceeded:
      'Денний ліміт повідомлень досягнуто. Спробуйте завтра.',
    errorBotTemporarilyUnavailable:
      'Чат-бот тимчасово недоступний. Спробуйте пізніше.',
    errorTurnstileFailed:
      'Перевірка не пройдена. Оновіть сторінку та спробуйте ще раз.',
  },
};

export function resolveLocale(attrValue: string | null): Locale {
  // Explicit attribute wins
  if (attrValue === 'uk' || attrValue === 'en') return attrValue;

  // Fallback to browser locale
  if (typeof navigator !== 'undefined' && navigator.language) {
    const prefix = navigator.language.toLowerCase().slice(0, 2);
    if (prefix === 'uk') return 'uk';
  }

  // Default
  return 'en';
}
